import { Card, ComponentState, Group, Icon, Modal, Number, Skeleton, Space, Text } from "components/common"
import { Col, List, Row } from "antd"
import { Fragment, forwardRef, useImperativeHandle, useMemo, useRef, useState } from "react"
import { IconApartment, IconHome } from "components/svg"
import FeatureLogsTable from "../featureLogsTable/featureLogsTable"
import Image from "components/common/image/image"
import InventoryDetail from "../inventoryDetail/inventoryDetail"
import InventoryNotes from "../inventoryNotes/inventoryNotes"
import UnitLogsTable from "../unitLogsTable/unitLogsTable"
import cx from "clsx"
import { useSelector } from "react-redux"
import { joinBytItems } from "utils"
import styles from "./index.module.less"
import { useLazyGetInventoryDetailQuery } from "../../api"
import { localisedValue } from "utils"
import getPrivilege from "utils/privileges/privileges"
import { t } from "i18next"
import MediaTab from "features/shared/components/mediaTab/mediaTab"

const InventoryModal = forwardRef((_props, ref) => {
  const [inventoryId, setInventoryId] = useState()
  const privileges = useSelector(state => state.app.privileges)
  const [tabKey, setTabKey] = useState(getPrivilege("can_view_unit_detail_page", privileges) ? "inventory" : "unitLogs")
  const modalRef = useRef()
  const [getInventoryDetails, { data: inventory, isFetching: isFetchingInventoryDetail, error: errorInventoryDetail }] =
    useLazyGetInventoryDetailQuery()

  useImperativeHandle(
    ref,
    () => ({
      showFor(id) {
        getInventoryDetails(id)
        setInventoryId(id)
        modalRef.current.setVisible(true)
      },
    }),
    []
  )
  const handleTabChange = key => setTabKey(key)

  const unitInfo = useMemo(() => {
    return [
      [t("Project") + ":", localisedValue({ object: inventory?.Project, key: "name" }) || "-"],
      [
        t("Price") + ":",
        <Number digitAfterFraction={1} isStringOnly isCurrency value={inventory?.price} tooltip={true} />,
      ],
      [t("Status") + ":", localisedValue({ object: inventory?.ListingStatus, key: "name" }) || "-"],
    ]
  }, [inventory])

  const getUnitDetail = info => {
    const unitInfoJsx = info.map((e, i) => (
      <Fragment key={i}>
        <span className="gray-800">{e[0]}</span>
        <strong>{e[1]}</strong>
      </Fragment>
    ))
    return joinBytItems(unitInfoJsx, <Icon icon="GoDotFill" size=".7em" color="#D6D6D6" />)
  }

  const tabItems = useMemo(
    () => [
      { tab: t("Inventory Detail"), key: "inventory", show: getPrivilege("can_view_unit_detail_page", privileges) },
      { tab: t("Unit Logs"), key: "unitLogs", show: getPrivilege("can_view_unit_logs_and_notes", privileges) },
      { tab: t("Feature Logs"), key: "featureLogs", show: getPrivilege("can_view_unit_logs_and_notes", privileges) },
      {
        tab: t("Inventory Notes"),
        key: "inventoryNotes",
        show: getPrivilege("can_view_unit_logs_and_notes", privileges),
      },
      {
        tab: t("Media"),
        key: "media",
        show: inventory?.Images?.hasImages,
      },
    ],
    [inventory, privileges]
  )
  const getTabContent = () => ({
    inventory: <InventoryDetail inventory={inventory} />,
    unitLogs: <UnitLogsTable inventoryId={inventoryId} />,
    featureLogs: <FeatureLogsTable inventoryId={inventoryId} />,
    inventoryNotes: <InventoryNotes inventoryId={inventoryId} />,
    media: <MediaTab data={inventory} />,
  })

  return (
    <Modal
      width={1000}
      ref={modalRef}
      onCancel={() => {
        modalRef?.current?.setVisible(false)
        setTabKey(getPrivilege("can_view_unit_detail_page", privileges) ? "inventory" : "unitLogs")
        setInventoryId(null)
      }}
      loading={isFetchingInventoryDetail}
      hideCancelButton={true}
      className={styles.modalBg}
      bodyStyle={{ padding: 24 }}
      footer={null}
    >
      {!!errorInventoryDetail ? (
        <ComponentState
          onClick={() => inventoryId && getInventoryDetails(inventoryId)}
          btnLoading={isFetchingInventoryDetail}
          description={errorInventoryDetail?.message}
          status={errorInventoryDetail?.status}
        />
      ) : isFetchingInventoryDetail ? (
        <InventorySkeleton />
      ) : (
        <>
          <Group template="auto 1fr" gap="24px" className="mbe-32 flexYstart">
            {inventory?.photos[0]?.src ? (
              <Image src={inventory?.photos[0]?.src} width={72} height={72} rootClassName={styles.img} />
            ) : (
              <Icon
                hasBackground
                icon="MdImage"
                color="#35A447"
                size="2.4em"
                iconContainerStyle={{ padding: 19, backgroundColor: "#D3F9DD" }}
              />
            )}
            <Group template="1fr" gap="16px">
              <Text color="#000" className="bold" block>
                {inventory?.unitNumber}
              </Text>
              <Space size={8}>{getUnitDetail(unitInfo)}</Space>
              <Row className={styles.productInfoMain}>
                {localisedValue({ object: inventory?.ProjectSection, key: "breadcrumbTitle" }) && (
                  <Col className={styles.productInfo}>
                    <IconHome size={20} className={cx("d-grid", styles.icon)} />
                    <small className="gray-800">Location</small>
                    <strong>{localisedValue({ object: inventory?.ProjectSection, key: "breadcrumbTitle" })}</strong>
                  </Col>
                )}
                {localisedValue({ object: inventory?.Type, key: "name" }) && (
                  <Col className={styles.productInfo}>
                    <IconApartment size={20} className={cx("d-grid", styles.icon)} />
                    <small className="gray-800">{t("Type")}</small>
                    <strong>{localisedValue({ object: inventory?.Type, key: "name" })}</strong>
                  </Col>
                )}
              </Row>
            </Group>
          </Group>
          <Card
            tabList={tabItems.filter(item => !!item.show)}
            activeTabKey={tabKey}
            onTabChange={handleTabChange}
            className={styles.tabsMain}
            style={{ boxShadow: "none", backgroundColor: "transparent" }}
            bodyStyle={{ padding: "24px 0 0", minHeight: 300 }}
            bordered={false}
          >
            {getTabContent()[tabKey]}
          </Card>
        </>
      )}
    </Modal>
  )
})

const InventorySkeleton = () => {
  return (
    <>
      <Group template="auto 1fr" gap="24px" className="mbe-32">
        <Skeleton type="image" />
        <Group template="45ch" gap="16px">
          <Skeleton type="text" />
          <Row className={styles.productInfoMain}>
            <Col className={styles.productInfo}>
              <Skeleton type="text" />
            </Col>
            <Col className={styles.productInfo}>
              <Skeleton type="text" />
            </Col>
            <Col className={styles.productInfo}>
              <Skeleton type="text" />
            </Col>
          </Row>
        </Group>
      </Group>
      <List size="small" className={cx(styles.listingMain, "mbe-32")}>
        {[1, 2, 3, 4, 5]?.map(e => (
          <List.Item>
            <Skeleton type="text" />
            <Skeleton type="text" />
          </List.Item>
        ))}
      </List>
    </>
  )
}

export default InventoryModal
